import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { useRoles } from '@/hooks/useRoles';
import { useRoleDefinitions } from '@/hooks/useRoleDefinitions';
import { useAuth } from './AuthContext';

interface CapabilitiesContextType {
  capabilities: Set<string>;
  hasCapability: (name: string) => boolean;
  isLoading: boolean;
}

const CapabilitiesContext = createContext<CapabilitiesContextType>({
  capabilities: new Set(),
  hasCapability: () => false,
  isLoading: true,
});

export const useCapabilities = () => useContext(CapabilitiesContext);

interface CapabilitiesProviderProps {
  children: React.ReactNode;
}

export const CapabilitiesProvider: React.FC<CapabilitiesProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { roles, isLoading: rolesLoading, fetchRoles } = useRoles();
  const { roleDefinitions, isLoading: defsLoading, fetchRoleDefinitions } = useRoleDefinitions();

  // Fetch both on mount when user is available
  useEffect(() => {
    if (user) {
      fetchRoles();
      fetchRoleDefinitions();
    }
  }, [user, fetchRoles, fetchRoleDefinitions]);

  const capabilities = useMemo(() => {
    const merged = new Set<string>();
    if (!roles.length || !roleDefinitions.length) return merged;

    // Recursively collect all role IDs from the nested tree
    const collectIds = (list: typeof roles): string[] =>
      list.flatMap(r => [String(r.id), ...collectIds(r.children)]);
    const userRoleIds = new Set(collectIds(roles));

    // Find matching definitions and OR-merge capabilities
    for (const def of roleDefinitions) {
      if (userRoleIds.has(String(def.id))) {
        for (const [cap, enabled] of Object.entries(def.capabilities)) {
          if (enabled) merged.add(cap);
        }
      }
    }

    return merged;
  }, [roles, roleDefinitions]);

  const hasCapability = useMemo(
    () => (name: string) => capabilities.has(name),
    [capabilities]
  );

  const value = useMemo(
    () => ({
      capabilities,
      hasCapability,
      isLoading: rolesLoading || defsLoading,
    }),
    [capabilities, hasCapability, rolesLoading, defsLoading]
  );

  return (
    <CapabilitiesContext.Provider value={value}>
      {children}
    </CapabilitiesContext.Provider>
  );
};
